/**
 * Vera Analytics route tree, mounted by the shell at /analytics.
 *
 * ProtectedRoute requires a Analytics.* role (global Admin passes). Everything
 * is read-only in v1; the claim-detail drill (member-level PHI) and the
 * source-ID workbench arrive in Phase 2 and ride the same gate.
 */

import { lazy, Suspense } from 'react';
import { Navigate, Outlet, type RouteObject } from 'react-router-dom';
import { Spinner } from '@fluentui/react-components';
import {
  ClassificationRegular,
  ClipboardTaskListLtrRegular,
  DataHistogramRegular,
  DatabaseRegular,
  LinkRegular,
  PeopleTeamRegular,
  ReceiptMoneyRegular,
  SearchRegular,
} from '@fluentui/react-icons';
import { AppShell, type NavEntry } from '@widgets/app-shell';
import { ErrorBoundary } from '@shared/ui';
import { ProtectedRoute } from '@shared/auth';
import { ANALYTICS_ACCENT } from '@analytics/ui/kit';

export { ANALYTICS_ACCENT };

const AnalyticsOverviewPage = lazy(() =>
  import('@analytics/pages/overview').then((m) => ({ default: m.AnalyticsOverviewPage }))
);
const AnalyticsEligibilityPage = lazy(() =>
  import('@analytics/pages/eligibility').then((m) => ({ default: m.AnalyticsEligibilityPage }))
);
const AnalyticsClaimsPage = lazy(() =>
  import('@analytics/pages/claims').then((m) => ({ default: m.AnalyticsClaimsPage }))
);
const AnalyticsLookupPage = lazy(() =>
  import('@analytics/pages/lookup').then((m) => ({ default: m.AnalyticsLookupPage }))
);
const AnalyticsClaimDetailPage = lazy(() =>
  import('@analytics/pages/claim-detail').then((m) => ({ default: m.AnalyticsClaimDetailPage }))
);
const AnalyticsSourceIdsPage = lazy(() =>
  import('@analytics/pages/source-ids').then((m) => ({ default: m.AnalyticsSourceIdsPage }))
);
const AnalyticsValidationPage = lazy(() =>
  import('@analytics/pages/validation').then((m) => ({ default: m.AnalyticsValidationPage }))
);
const AnalyticsClassificationPage = lazy(() =>
  import('@analytics/pages/classification').then((m) => ({ default: m.AnalyticsClassificationPage }))
);
const AnalyticsSchemaPage = lazy(() =>
  import('@analytics/pages/schema').then((m) => ({ default: m.AnalyticsSchemaPage }))
);
const AnalyticsMemberDetailPage = lazy(() =>
  import('@analytics/pages/member-detail').then((m) => ({ default: m.AnalyticsMemberDetailPage }))
);

function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<Spinner style={{ padding: '2rem' }} label="Loading..." />}>{children}</Suspense>;
}

function AnalyticsLayout({ basePath }: { basePath: string }) {
  const nav: NavEntry[] = [
    { path: `${basePath}/overview`, label: 'Overview', icon: DataHistogramRegular },
    { path: `${basePath}/eligibility`, label: 'Eligibility', icon: PeopleTeamRegular },
    { path: `${basePath}/claims`, label: 'Claims', icon: ReceiptMoneyRegular },
    { path: `${basePath}/lookup`, label: 'Claim lookup', icon: SearchRegular },
    { path: `${basePath}/validation`, label: 'Group validation', icon: ClipboardTaskListLtrRegular },
    { path: `${basePath}/classification`, label: 'Classification (POC)', icon: ClassificationRegular },
    { path: `${basePath}/source-ids`, label: 'Source IDs', icon: LinkRegular },
    { path: `${basePath}/schema`, label: 'Schema', icon: DatabaseRegular },
  ];

  return (
    <ProtectedRoute appRolePrefix="Analytics">
      <AppShell appId="analytics" appTitle="Claims Analysis" navItems={nav} headerColor={ANALYTICS_ACCENT}>
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </AppShell>
    </ProtectedRoute>
  );
}

export function analyticsRoutes(basePath: string): RouteObject[] {
  return [
    {
      element: <AnalyticsLayout basePath={basePath} />,
      children: [
        { index: true, element: <Navigate to="overview" replace /> },
        {
          path: 'overview',
          element: (
            <LazyPage>
              <AnalyticsOverviewPage />
            </LazyPage>
          ),
        },
        {
          path: 'eligibility',
          element: (
            <LazyPage>
              <AnalyticsEligibilityPage />
            </LazyPage>
          ),
        },
        {
          path: 'claims',
          element: (
            <LazyPage>
              <AnalyticsClaimsPage />
            </LazyPage>
          ),
        },
        {
          path: 'claims/:claimId',
          element: (
            <LazyPage>
              <AnalyticsClaimDetailPage />
            </LazyPage>
          ),
        },
        {
          path: 'lookup',
          element: (
            <LazyPage>
              <AnalyticsLookupPage />
            </LazyPage>
          ),
        },
        {
          path: 'classification',
          element: (
            <LazyPage>
              <AnalyticsClassificationPage />
            </LazyPage>
          ),
        },
        {
          path: 'source-ids',
          element: (
            <LazyPage>
              <AnalyticsSourceIdsPage />
            </LazyPage>
          ),
        },
        {
          path: 'schema',
          element: (
            <LazyPage>
              <AnalyticsSchemaPage />
            </LazyPage>
          ),
        },
        {
          path: 'validation',
          element: (
            <LazyPage>
              <AnalyticsValidationPage />
            </LazyPage>
          ),
        },
        {
          path: 'members/:memberId',
          element: (
            <LazyPage>
              <AnalyticsMemberDetailPage />
            </LazyPage>
          ),
        },
      ],
    },
  ];
}
